import React from 'react';
import {Box, Link, Typography} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTime } from 'luxon';

const SnackBar = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {snackBarInfo} = props;

  // If snackBarInfo is missing or empty, return null (don't render anything)
  if (!snackBarInfo || Object.keys(snackBarInfo).length === 0) {
    return null;
  }

  const {image, backgroundColor, textColor, text, url, expirationDate, linkText} = snackBarInfo;
  
  // Check if the snackBar has expired
  if (expirationDate) {
    // Get current time in Eastern Time
    const currentDate = DateTime.now().setZone('America/New_York');
    
    // Parse expiration date as Eastern Time
    // Assuming expirationDate is provided in ISO format or a format Luxon can parse
    const expireDate = DateTime.fromISO(expirationDate, { zone: 'America/New_York' });
    
    // If current date is after expiration date, don't render the snackbar
    if (currentDate > expireDate) {
      return null;
    }
  }

  // Image styles
  const imageStyles = {
    height: isMobile ? '32px' : '40px',
    width: isMobile ? '32px' : '40px',
    marginRight: '8px',
    objectFit: 'contain',
  };

  // Common outer container styles (full width background)
  const outerContainerStyles = {
    backgroundColor: backgroundColor,
    color: textColor,
    padding: '16px',
    paddingTop: '22px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center', // Center the inner container
    alignItems: 'center',
    fontSize: isMobile ? '12px' : '16px',
    fontWeight: 700,
    zIndex: 1000,
    top: isMobile ? 80 : 136,
    left: 0,
  };

  // Common inner container styles (max-width content)
  const innerContainerStyles = {
    maxWidth: '500px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  };

  // Conditionally render based on URL presence
  if (url) {
    // With URL: Left aligned content and VIEW on right
    return (
      <Link
        href={url}
        sx={{
          textDecoration: 'none',
          display: 'block',
          width: '100%',
        }}
      >
        <Box sx={outerContainerStyles}>
          <Box sx={{
            ...innerContainerStyles,
            justifyContent: 'space-between', // Space between for URL case
          }}>
            {/* Left side with image and text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {image && (
                <img src={image} alt="Snack Bar Icon" style={imageStyles} />
              )}
              <span>{text}</span>
            </Box>
            
            {/* Right side with VIEW text */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? '12px' : '16px',
                color: textColor,
              }}
            >
              {linkText ? linkText : 'View'}
            </Typography>
          </Box>
        </Box>
      </Link>
    );
  } else {
    // Without URL: Centered content
    return (
      <Box sx={outerContainerStyles}>
        <Box sx={{
          ...innerContainerStyles,
          justifyContent: 'center', // Center for non-URL case
        }}>
          {/* Centered image and text */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {image && (
              <img src={image} alt="Snack Bar Icon" style={imageStyles} />
            )}
            <span>{text}</span>
          </Box>
        </Box>
      </Box>
    );
  }
};

export default SnackBar;